import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaLock,
  FaScroll,
  FaTimes,
  FaTimesCircle,
  FaTrashAlt,
  FaUserShield,
} from 'react-icons/fa';
import { useTheme } from '../themeConfig';

const BRAND_NAME = 'Lumirae';
const CONTACT_EMAIL = 'hi@thelumirae.com';

const POLICY_SECTIONS = [
  {
    title: 'What Lumirae Is',
    body: 'Lumirae lets a page owner create a personal digital gift page with photos, videos, audio, written messages, guestbook notes, and optional visitor reactions. Anyone with the page link may be able to view the page content unless the page owner deletes or restricts it.',
  },
  {
    title: 'Information We Collect',
    body: 'We collect the information needed to provide the service, including page content, uploaded media, guestbook names and messages, reaction uploads, admin authentication data, basic device or browser information, page URLs, referrers, timestamps, and abuse-prevention records such as upload tickets and reports.',
  },
  {
    title: 'How We Use Information',
    body: 'We use information to host and display gift pages, save edits, process uploads, operate guestbook and reaction features, protect accounts, prevent spam or abuse, troubleshoot service issues, and respond to deletion, safety, or support requests.',
  },
  {
    title: 'Sharing and Visibility',
    body: 'Gift pages are link-access pages, not public directory pages. We do not present them as a public social feed, but anyone who receives, guesses, forwards, screenshots, or otherwise obtains the page URL may be able to view the gift content. We do not sell personal information.',
  },
  {
    title: 'User Uploads and Guest Content',
    body: 'Do not upload content that is illegal, hateful, explicit, harassing, invasive of privacy, or that you do not have permission to share. Do not post addresses, phone numbers, financial details, medical details, school information, or other sensitive information. Guestbook messages may be reviewed, hidden, reported, or removed.',
  },
  {
    title: 'Children and Family Content',
    body: 'Lumirae is not directed to children under 13. Children under 13 should not submit information or uploads. If a gift page includes photos, videos, names, or stories about a child, the page owner is responsible for having the right consent and for avoiding sensitive child information.',
    note: "For baby, kids, family, school, health, or memorial pages, share links carefully and avoid posting a child's full name, school, address, daily routine, medical details, exact location, or anything you would not want forwarded outside your trusted circle.",
  },
  {
    title: 'California Privacy Notice',
    body: 'California residents may have rights to know, access, correct, delete, and limit certain uses of personal information, and the right not to be discriminated against for exercising privacy rights. We do not sell personal information. To make a privacy request, contact us at the email below.',
  },
  {
    title: 'Retention and Deletion',
    body: 'We keep content while the page remains active or as needed for service, security, legal, and backup purposes. Page administrators can delete a page, which is intended to remove the page, page secrets, guest notes, upload tickets, and related media. You may contact us to request review or deletion of specific content.',
  },
  {
    title: 'Security',
    body: 'We use Firebase security rules, server-side functions, password protection, upload limits, and access controls to help protect content. No online service is perfectly secure, so page administrators should use unique passwords and share page links carefully.',
  },
  {
    title: 'Sharing Choices',
    body: 'Page owners can choose whether a recipient sees full-link sharing, share-card-only sharing, or no sharing controls. Turning sharing controls off hides UI buttons; it does not revoke links that were already shared and it does not add a viewer password.',
  },
];

const TRUST_POINTS = [
  {icon: FaEye, label: 'Link-access page', detail: 'Anyone with the URL or NFC can view.'},
  {icon: FaUserShield, label: 'No sale of personal info', detail: 'We use data to operate and protect the service.'},
  {icon: FaTrashAlt, label: 'Deletion requests', detail: 'Admins and users may request content review or removal.'},
];

const PrivacyPolicyModal = ({ isOpen, onAccept, onReject, infoOnly = false }) => {
  const { theme } = useTheme();

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 p-4 backdrop-blur-md">
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 18 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 18 }}
            className="lumirae-ds-panel flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden shadow-2xl"
            style={{
              backgroundColor: theme.cardBg,
              borderColor: theme.cardBorder,
              color: theme.text,
            }}
          >
            <div
              className="relative overflow-hidden border-b px-5 py-5 sm:px-7"
              style={{borderColor: theme.cardBorder, backgroundColor: theme.inputBg}}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[1.15rem] text-white shadow-lg" style={{backgroundColor: theme.accent}}>
                    <FaScroll />
                  </div>
                  <div className="min-w-0 pr-2 sm:pr-10">
                    <p className="lumirae-ds-meta" style={{color: theme.accent}}>Privacy Center</p>
                    <h2 className="mt-1 text-xl font-black tracking-tight sm:text-2xl">
                      {BRAND_NAME} Privacy & Terms
                    </h2>
                    <p className="mt-2 max-w-xl text-xs font-medium leading-relaxed" style={{color: theme.textMuted}}>
                      Clear rules for personal gift pages, NFC access, guest messages, uploads, and deletion choices.
                    </p>
                  </div>
                </div>
                {infoOnly && (
                  <button
                    type="button"
                    onClick={onAccept}
                    className="lumirae-ds-icon-button h-10 w-10 shrink-0"
                    aria-label="Close privacy policy"
                    title="Close"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>

              <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {TRUST_POINTS.map(({icon: Icon, label, detail}) => (
                  <div key={label} className="rounded-[1rem] border p-3" style={{borderColor: theme.cardBorder, backgroundColor: theme.cardBg}}>
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest" style={{color: theme.accent}}>
                      <Icon /> {label}
                    </div>
                    <p className="mt-2 text-[11px] font-bold leading-relaxed" style={{color: theme.textMuted}}>{detail}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-7">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {POLICY_SECTIONS.map((section, index) => (
                  <section key={section.title} className="rounded-[1.25rem] border p-4" style={{borderColor: theme.cardBorder, backgroundColor: index % 2 === 0 ? theme.inputBg : theme.cardBg}}>
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-black text-white" style={{backgroundColor: theme.accent}}>
                        {index + 1}
                      </span>
                      <div>
                        <h3 className="text-sm font-black tracking-tight">{section.title}</h3>
                        <p className="mt-2 text-xs font-bold leading-relaxed" style={{color: theme.textMuted}}>
                          {section.body}
                        </p>
                        {section.note && (
                          <div className="mt-3 rounded-[1rem] border p-3 text-xs font-bold leading-relaxed" style={{borderColor: theme.cardBorder, backgroundColor: theme.accentSoft, color: theme.text}}>
                            <FaExclamationTriangle className="mr-2 inline" style={{color: theme.accent}} />
                            {section.note}
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-5 rounded-[1.25rem] border p-4 text-xs font-bold leading-relaxed" style={{borderColor: theme.cardBorder, backgroundColor: theme.inputBg, color: theme.textMuted}}>
                <p className="font-black uppercase tracking-widest" style={{color: theme.accent}}>Last Updated: July 17, 2026</p>
                <p className="mt-1">Contact: {CONTACT_EMAIL}</p>
              </div>
            </div>

            <div className="border-t px-5 py-4 sm:px-7" style={{borderColor: theme.cardBorder, backgroundColor: theme.inputBg}}>
              {infoOnly ? (
                <button
                  type="button"
                  onClick={onAccept}
                  className="lumirae-ds-button lumirae-ds-button-primary flex w-full items-center justify-center gap-2 px-5 py-4"
                >
                  <FaCheckCircle /> Close
                </button>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={onReject}
                    className="lumirae-ds-button lumirae-ds-button-danger flex items-center justify-center gap-2 px-5 py-4"
                  >
                    <FaTimesCircle /> Disagree
                  </button>

                  <button
                    type="button"
                    onClick={onAccept}
                    className="lumirae-ds-button lumirae-ds-button-primary flex items-center justify-center gap-2 px-5 py-4"
                  >
                    <FaLock /> Agree & Continue
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PrivacyPolicyModal;
